<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <h3 class="report-title">Winnings by Place</h3>

        <!-- Date range buttons -->
        <div class="d-flex justify-center flex-wrap mb-2" style="gap: 6px;">
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'all'"    @click="setAllTime">All Time</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'ytd'"    @click="setYearToDate">YTD</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'last12'" @click="setLast12Months">Last 12</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'custom'" @click="setCustom">Custom</v-btn>
        </div>

        <!-- Custom date pickers -->
        <div v-if="dateMode === 'custom'" class="d-flex justify-center flex-wrap mb-3" style="gap: 8px;">
          <v-menu v-model="fromDateMenu" :close-on-content-click="false" min-width="auto">
            <template #activator="{ props }">
              <v-text-field
                :model-value="fromDate ? formatDate(fromDate) : ''"
                @update:model-value="val => fromDate = parseDate(val)"
                label="From" prepend-icon="mdi-calendar" readonly v-bind="props"
                style="max-width: 180px;" density="compact" hide-details
              />
            </template>
            <v-date-picker v-model="fromDate" @update:model-value="fromDateMenu = false" show-adjacent-months />
          </v-menu>
          <v-menu v-model="toDateMenu" :close-on-content-click="false" min-width="auto">
            <template #activator="{ props }">
              <v-text-field
                :model-value="toDate ? formatDate(toDate) : ''"
                @update:model-value="val => toDate = parseDate(val)"
                label="To" prepend-icon="mdi-calendar" readonly v-bind="props"
                style="max-width: 180px;" density="compact" hide-details
              />
            </template>
            <v-date-picker v-model="toDate" @update:model-value="toDateMenu = false" show-adjacent-months />
          </v-menu>
        </div>

        <!-- Session type filter -->
        <div class="d-flex justify-center flex-wrap mb-3" style="gap: 6px;">
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === null"       @click="typefilter = null">All</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === CASH"       @click="typefilter = CASH">Cash</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === TOURNAMENT" @click="typefilter = TOURNAMENT">Tournament</v-btn>
        </div>

        <!-- View mode toggle -->
        <div class="d-flex justify-center mb-3" style="gap: 8px;">
          <v-btn size="small" :variant="viewMode === 'place' ? 'tonal' : 'outlined'" color="primary" @click="viewMode = 'place'">By Place</v-btn>
          <v-btn size="small" :variant="viewMode === 'group' ? 'tonal' : 'outlined'" color="primary" @click="viewMode = 'group'" :disabled="placeGroups.length === 0">By Group</v-btn>
        </div>

        <div v-if="rows.length > 0">
          <!-- By Place table -->
          <v-table v-if="viewMode === 'place'" density="compact">
            <thead>
              <tr>
                <th class="text-left">Place</th>
                <th class="text-right">Sessions</th>
                <th class="text-right">Total Net</th>
                <th class="text-right">Avg / Session</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.place" class="clickable-row" @click="handlePlaceClick(row.place)">
                <td class="text-left">{{ row.place }}</td>
                <td class="text-right">{{ row.sessions }}</td>
                <td class="text-right" :class="row.net >= 0 ? 'green-text' : 'red-text'">{{ formatNet(row.net) }}</td>
                <td class="text-right" :class="row.avg >= 0 ? 'green-text' : 'red-text'">{{ formatNet(row.avg) }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- By Group table -->
          <v-table v-else density="compact">
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-right">Sessions</th>
                <th class="text-right">Total Net</th>
                <th class="text-right">Avg / Session</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in groupedRows" :key="row.type === 'group' ? 'g:' + row.name : 'p:' + row.place">
                <tr v-if="row.type === 'group'" class="clickable-row group-row" @click="toggleGroup(row.name)">
                  <td class="text-left">
                    <v-icon size="x-small" class="mr-1">{{ expandedGroups.has(row.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                    <strong>{{ row.name }}</strong>
                  </td>
                  <td class="text-right"><strong>{{ row.sessions }}</strong></td>
                  <td class="text-right" :class="row.net >= 0 ? 'green-text' : 'red-text'"><strong>{{ formatNet(row.net) }}</strong></td>
                  <td class="text-right" :class="row.avg >= 0 ? 'green-text' : 'red-text'"><strong>{{ formatNet(row.avg) }}</strong></td>
                </tr>
                <template v-if="row.type === 'group' && expandedGroups.has(row.name)">
                  <tr v-for="sub in row.subRows" :key="sub.place" class="clickable-row sub-row" @click.stop="handlePlaceClick(sub.place)">
                    <td class="text-left pl-6">↳ {{ sub.place }}</td>
                    <td class="text-right">{{ sub.sessions }}</td>
                    <td class="text-right" :class="sub.net >= 0 ? 'green-text' : 'red-text'">{{ formatNet(sub.net) }}</td>
                    <td class="text-right" :class="sub.avg >= 0 ? 'green-text' : 'red-text'">{{ formatNet(sub.avg) }}</td>
                  </tr>
                </template>
                <tr v-if="row.type === 'place'" class="clickable-row" @click="handlePlaceClick(row.place)">
                  <td class="text-left">{{ row.place }}</td>
                  <td class="text-right">{{ row.sessions }}</td>
                  <td class="text-right" :class="row.net >= 0 ? 'green-text' : 'red-text'">{{ formatNet(row.net) }}</td>
                  <td class="text-right" :class="row.avg >= 0 ? 'green-text' : 'red-text'">{{ formatNet(row.avg) }}</td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </div>
        <div v-else class="text-center text-medium-emphasis py-4">
          No sessions in this date range.
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useSessionStore } from '@/stores/sessions';
import { useUserStore } from '@/stores/users';
import { useDateFilter } from '@/compostables/useDateFilter';
import { CASH, TOURNAMENT } from '@/constants/constants';

const emit = defineEmits(['drilldown']);

const sessionStore = useSessionStore();
const userStore = useUserStore();
const placeGroups = computed(() => userStore.placeGroups);

const sessionlist = computed(() => sessionStore.sessionlist);

const {
  dateMode, fromDate, toDate, fromDateMenu, toDateMenu,
  filteredSessions, setAllTime, setYearToDate, setLast12Months, setCustom,
  formatDate, parseDate,
} = useDateFilter(sessionlist);

const viewMode = ref('place');
const expandedGroups = ref(new Set());
const typefilter = ref(null);

const typefilteredSessions = computed(() =>
  typefilter.value ? filteredSessions.value.filter(s => s.type === typefilter.value) : filteredSessions.value
);

const rows = computed(() => {
  const map = {};
  for (const s of typefilteredSessions.value) {
    const place = s.place || 'Unknown';
    if (!map[place]) map[place] = { place, net: 0, sessions: 0 };
    map[place].net += s.net ?? 0;
    map[place].sessions += 1;
  }
  return Object.values(map)
    .map(r => ({ ...r, avg: r.sessions > 0 ? r.net / r.sessions : 0 }))
    .sort((a, b) => b.net - a.net);
});

const groupedRows = computed(() => {
  const map = {};
  for (const s of typefilteredSessions.value) {
    const place = s.place || 'Unknown';
    if (!map[place]) map[place] = { place, net: 0, sessions: 0 };
    map[place].net += s.net ?? 0;
    map[place].sessions += 1;
  }

  const groupedPlaces = new Set(placeGroups.value.flatMap(g => g.places));

  const groupRows = placeGroups.value
    .filter(g => g.places.some(p => map[p]))
    .map(g => {
      const subRows = g.places
        .filter(p => map[p])
        .map(p => {
          const r = map[p];
          return { type: 'place', place: p, net: r.net, sessions: r.sessions, avg: r.sessions > 0 ? r.net / r.sessions : 0 };
        })
        .sort((a, b) => b.net - a.net);
      const net = subRows.reduce((s, r) => s + r.net, 0);
      const sessions = subRows.reduce((s, r) => s + r.sessions, 0);
      return { type: 'group', name: g.name, net, sessions, avg: sessions > 0 ? net / sessions : 0, subRows };
    })
    .sort((a, b) => b.net - a.net);

  const ungrouped = Object.values(map)
    .filter(r => !groupedPlaces.has(r.place))
    .map(r => ({ type: 'place', ...r, avg: r.sessions > 0 ? r.net / r.sessions : 0 }))
    .sort((a, b) => b.net - a.net);

  return [...groupRows, ...ungrouped];
});

function toggleGroup(name) {
  const next = new Set(expandedGroups.value);
  if (next.has(name)) next.delete(name);
  else next.add(name);
  expandedGroups.value = next;
}

function handlePlaceClick(place) {
  emit('drilldown', {
    fromDate:    { date: new Date(fromDate.value) },
    toDate:      { date: new Date(toDate.value) },
    typefilter:  typefilter.value,
    gamefilter:  [],
    placefilter: [place],
    bettypefilter: [],
  });
}

function formatNet(val) {
  const n = Number(val).toFixed(2);
  return val >= 0 ? `$${n}` : `-$${Math.abs(n).toFixed(2)}`;
}
</script>

<style scoped>
.report-title {
  margin-bottom: 12px;
  text-align: center;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover td {
  background-color: rgba(0, 0, 0, 0.04);
}
.group-row td {
  background-color: rgba(0, 0, 0, 0.02);
}
.sub-row td {
  font-size: 0.875em;
  color: rgba(0, 0, 0, 0.7);
}
.green-text { color: #2e7d32; }
.red-text   { color: #c62828; }
</style>
